import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

export default function UseWorkoutOnOtherDaysModal({
  isOpen,
  sourceWeekdayName,
  days = [],
  onConfirm,
  onClose,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds([]);
    setIsSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, isSubmitting]);

  if (!isOpen) return null;

  const selectable = days.filter((d) => !d.hasSamePlan);
  const replaceCount = selectedIds.filter((id) =>
    days.some((d) => String(d.weekdayId) === String(id) && d.hasOtherPlan)
  ).length;

  const toggle = (weekdayId) => {
    const id = String(weekdayId);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = async () => {
    if (!selectedIds.length || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedIds);
      onClose();
    } catch {
      // Parent shows error; keep open for retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => !isSubmitting && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="use-on-other-days-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 id="use-on-other-days-title" className="text-lg font-bold text-gray-900">
              Assign to other days
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Use this workout{sourceWeekdayName ? ` from ${sourceWeekdayName}` : ''} on more
              weekdays.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {selectable.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {days.length === 0
                ? 'No other days in this schedule.'
                : 'This plan is already used on every other day.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {days.map((day) => {
                const id = String(day.weekdayId);
                const checked = selectedIds.includes(id);
                const disabled = day.hasSamePlan || isSubmitting;
                return (
                  <li key={id}>
                    <label
                      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                        disabled
                          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                          : checked
                            ? 'border-orange-primary bg-orange-50'
                            : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(day.weekdayId)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900 capitalize">
                          {day.name}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {day.hasSamePlan
                            ? 'Already uses this plan'
                            : day.hasOtherPlan
                              ? `Replaces ${day.existingTitle || 'current plan'}`
                              : 'Rest day'}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {replaceCount > 0 && (
          <p className="px-5 pb-2 text-xs text-amber-700">
            {replaceCount} selected day{replaceCount === 1 ? '' : 's'} will replace an existing
            plan.
          </p>
        )}

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || selectedIds.length === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting
              ? 'Assigning…'
              : selectedIds.length
                ? `Assign to ${selectedIds.length} day${selectedIds.length === 1 ? '' : 's'}`
                : 'Select days'}
          </button>
        </div>
      </div>
    </div>
  );
}
